import React, { useEffect, useState } from "react";
import { fmtDateTime } from "@/lib/dateUtils";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CalendarClock, Users, Mail, Plus, GraduationCap, ArrowUpRight, ReceiptText } from "lucide-react";

export default function ParentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLink, setShowLink] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/parent/dashboard");
      setData(data);
    } catch (e) { toast.error("Could not load dashboard"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-sm text-neutral-500">Loading…</div>;

  const children = data?.children || [];
  const upcoming = data?.upcoming_classes || [];

  return (
    <div className="space-y-8" data-testid="parent-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ parent overview</div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter">Hello, {user?.full_name?.split(" ")[0]}.</h1>
          <p className="text-sm text-neutral-600 mt-1">Quiet, honest updates from your child's classroom.</p>
        </div>
        <button onClick={() => setShowLink(true)} data-testid="link-child-btn" className="inline-flex items-center gap-2 bg-[#FFD600] text-[#0A0C10] font-bold px-5 py-2.5 btn-brutal-shadow">
          <Plus size={16} /> Link another child
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Stat label="Linked children" value={children.length} icon={Users} />
        <Stat label="Upcoming classes" value={upcoming.length} icon={CalendarClock} />
        <Stat label="Pending fees" value={`$${(data?.pending_fees_total || 0).toFixed(0)}`} icon={ReceiptText} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold">Your children</div>
          </div>
          {children.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6 text-center border border-dashed border-neutral-200">
              No children linked yet.<br />
              <button onClick={() => setShowLink(true)} className="mt-2 text-[#002FA7] underline">Link your first child</button>
            </div>
          ) : children.map((c) => (
            <div key={c.id} className="py-3 border-b border-neutral-100 last:border-0 flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">{c.full_name}</div>
                <div className="text-xs text-neutral-500 inline-flex items-center gap-2"><GraduationCap size={12} /> {c.grade || "—"} · {c.email}</div>
              </div>
              <Link to={`/parent/child/${c.id}`} data-testid={`open-child-${c.id}`} className="text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] px-3 py-1.5 inline-flex items-center gap-1">
                Open <ArrowUpRight size={12} />
              </Link>
            </div>
          ))}
        </div>

        <div className="bg-white border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold">Upcoming classes</div>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6 text-center border border-dashed border-neutral-200">No classes coming up.</div>
          ) : upcoming.slice(0, 6).map((c) => (
            <div key={c.id} className="py-3 border-b border-neutral-100 last:border-0">
              <div className="font-medium text-sm">{c.title}</div>
              <div className="text-xs text-neutral-500">{c.subject} · {fmtDateTime(c.start_time)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#0A0C10] text-white p-6 noise-overlay">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-400 mb-2">/ weekly digest</div>
        <h2 className="font-display font-bold text-2xl tracking-tighter">A calm Saturday brief, every week.</h2>
        <p className="text-sm text-neutral-300 mt-2">We email a short summary of attendance, classwork, and any fees due — straight to your inbox.</p>
        <div className="mt-4 text-xs text-neutral-400 inline-flex items-center gap-2"><Mail size={12} className="text-[#FFD600]" /> Delivered to {user?.email}</div>
      </div>

      {showLink && <LinkChildModal onClose={() => setShowLink(false)} onLinked={() => { setShowLink(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-neutral-200 p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">{label}</div>
        <Icon size={16} className="text-neutral-400" />
      </div>
      <div className="font-display font-bold text-3xl mt-2">{value}</div>
    </div>
  );
}

function LinkChildModal({ onClose, onLinked }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/parent/link-child", { child_email: email });
      toast.success(data.already_linked ? "Already linked" : `Linked ${data.child_name || "child"}`);
      onLinked();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not link"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white w-full max-w-md p-6">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ link child</div>
        <h2 className="font-display font-bold text-2xl tracking-tight">Link a student to your account</h2>
        <p className="text-sm text-neutral-600 mt-1 mb-4">Enter your child's student account email.</p>
        <input data-testid="link-child-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="student@example.com" className="w-full bg-white border border-neutral-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#002FA7]" />
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 border border-neutral-300">Cancel</button>
          <button type="submit" disabled={loading} data-testid="link-child-submit" className="bg-[#0A0C10] text-white font-bold px-4 py-2 disabled:opacity-60">{loading ? "Linking…" : "Link"}</button>
        </div>
      </form>
    </div>
  );
}
